import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { adminDeletePackage, adminListPackages } from '../../api/packages';
import { formatINR, todayIso } from '../../shared/data/common';
import Icon from '../../shared/components/Icons';
import { usePackages } from '../packages/PackagesContext';
import AdminShell, { useAdminAccess } from './AdminShell';
import { categories, categoryInfo } from './packageForm';

const nextDeparture = (pkg) => pkg.departures.map((d) => d.date).filter((d) => d >= todayIso()).sort()[0];

export default function AdminPackagesPage() {
  const { auth } = useAuth();
  const hasAccess = useAdminAccess();
  const { reload: reloadSite } = usePackages();
  const { state } = useLocation();
  const [params, setParams] = useSearchParams();
  const category = params.get('category') || '';
  const [list, setList] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      setError('');
      setList(await adminListPackages(auth.accessToken, category));
    } catch (requestError) {
      setError(requestError.message);
    }
  }, [auth?.accessToken, category]);

  useEffect(() => {
    if (hasAccess) load();
  }, [hasAccess, load]);

  const remove = async (pkg) => {
    if (!window.confirm(`Delete "${pkg.title}"? Its enquiries will be deleted too.`)) return;
    setBusyId(pkg.id);
    setError('');
    try {
      await adminDeletePackage(auth.accessToken, pkg.id);
      await load();
      reloadSite();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <AdminShell
      title="Holiday packages"
      subtitle="Create and manage group tours, family packages and customized packages."
      actions={categories.map((c) => (
        <Link key={c} to={`/admin/packages/new?category=${c}`} className="btn btn-primary btn-sm">
          <Icon name="packages" size={16} /> New {categoryInfo[c].label.toLowerCase()}
        </Link>
      ))}
    >
      {state?.message && <p className="api-message success" role="status">{state.message}</p>}
      <div className="underline-tabs admin-tabs">
        {[['', 'All'], ...categories.map((c) => [c, categoryInfo[c].plural])].map(([key, label]) => (
          <button key={key || 'all'} className={category === key ? 'active' : ''}
            onClick={() => setParams(key ? { category: key } : {})}>{label}</button>
        ))}
      </div>
      {error && <p className="api-message error" role="alert">{error}</p>}
      {!list && !error && <p role="status">Loading packages…</p>}
      {list && (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead><tr><th>Package</th><th>Type</th><th>Destination</th><th>Duration</th><th>Price / adult</th><th>Next departure</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {list.map((pkg) => (
                <tr key={pkg.id}>
                  <td><b>{pkg.title}</b></td>
                  <td>{categoryInfo[pkg.category]?.label || pkg.category}</td>
                  <td>{pkg.destination}{pkg.isInternational ? ' · Intl' : ''}</td>
                  <td>{pkg.nights}N / {pkg.nights + 1}D</td>
                  <td>{pkg.category === 'Customized' ? 'From ' : ''}{formatINR(pkg.pricePerAdult)}</td>
                  <td>{pkg.category === 'Group' ? nextDeparture(pkg) || 'None upcoming' : '—'}</td>
                  <td><span className={`status-pill ${pkg.isPublished ? 'live' : ''}`}>{pkg.isPublished ? 'Published' : 'Draft'}</span></td>
                  <td className="admin-row-actions">
                    <Link className="btn btn-outline btn-sm" to={`/admin/packages/${pkg.id}/edit`}>Edit</Link>
                    {pkg.isPublished && <Link className="btn btn-outline btn-sm" to={`/packages/${pkg.id}`}>View</Link>}
                    <button className="btn btn-outline btn-sm danger" disabled={busyId === pkg.id} onClick={() => remove(pkg)}>Delete</button>
                  </td>
                </tr>
              ))}
              {!list.length && <tr><td colSpan="8">No packages yet. Use the buttons above to add one.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </AdminShell>
  );
}
